'use client';

import { useState, useEffect, Suspense, type FormEvent } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { toast } from 'sonner';
import { api, tokenStore, ApiClientError } from '@/lib/api-client';
import type { FeatureFlags } from '@profileia/shared';
import { registerSchema, formatZodErrors } from '@/lib/validators';
import Link from 'next/link';
// D-LOW-003 fix (SPEC-044 QA AUDIT R1 — Batch 4): explicit `<Label htmlFor>` +
// `<Input id>` replaces implicit `<label><span>X</span><input/></label>`.
// See forgot-password/page.tsx for the full accessibility rationale
// (WCAG 2.1 SC 1.3.1 + SC 4.1.2).
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
// SPEC-050 Bug 1: replace native `<button>` with shadcn `<Button>` so the
// auth action inherits theme-aware tokens (`bg-primary`, `text-primary-foreground`,
// `focus-visible:ring-ring`) + the Bug 8 mobile touch-target sizing.
import { Button } from '@/components/ui/button';
// SPEC-088 §2.1 — T&C checkbox. Reuses the existing `Checkbox` shadcn
// component (same one used in checkout-modal.tsx + packages page). The
// checkbox is REQUIRED before the submit button is enabled — pure UX
// gating, no business logic. The label links to `/legal/terminos`
// (target="_blank") so the user can read the T&C without losing their
// form state.
import { Checkbox } from '@/components/ui/checkbox';
// P-197 (SPEC-131): ExternalLink icon next to the "Términos y Condiciones"
// link — signals to the user that the link opens in a new tab. ChevronLeft
// is used by the "Volver al inicio" link added at the top of the page.
import { ChevronLeft, ExternalLink, Eye, EyeOff, Loader2, Check, X } from 'lucide-react';

/**
 * Register page — wraps the form in a Suspense boundary because useSearchParams()
 * requires it during static prerender (Next.js 16 requirement).
 * F-26: Reads ?ref=CODE from URL to prefill referral code.
 */
export default function RegisterPage() {
  return (
    <Suspense fallback={<RegisterFallback />}>
      <RegisterContent />
    </Suspense>
  );
}

function RegisterFallback() {
  return (
    <main id="main-content" className="min-h-screen flex items-center justify-center px-4 py-12 bg-background">
      <div className="text-muted-foreground">Cargando…</div>
    </main>
  );
}

const FALSE_FLAGS_PROXY = new Proxy({} as FeatureFlags, { get: () => false });

function RegisterContent() {
  const router = useRouter();
  const params = useSearchParams();
  // F-26: Read ?ref=CODE from URL to prefill referral code
  const refFromUrl = params.get('ref') ?? '';
  // SPEC-050 Bug 5: Read ?package=<tier> from URL. The /paquetes public
  // pricing page links "Comenzar" CTAs to `/register?package=<tier>` so
  // the user arrives here with a pre-selected package. We:
  //   (1) validate it's a real tier (defense-in-depth — the /paquetes
  //       page only emits known tiers, but a user could craft a URL);
  //   (2) show a small banner so the user sees which package they picked;
  //   (3) stash it in `sessionStorage` so it survives the page reload
  //       that happens after a successful registration (the redirect
  //       to `/app/packages?preselect=<tier>` is decided at registration
  //       completion, not at mount — so we need to persist across the
  //       redirect).
  // `gratuito` is a valid preselect (the user picked the free tier) but
  // there's nothing to "purchase" — we still keep it for the toast /
  // banner but the /app/packages page ignores it (gratuito is the
  // default state after registration).
  const packageFromUrl = params.get('package') ?? '';
  const VALID_TIERS = ['gratuito', 'basic', 'bronce', 'plata', 'oro', 'platino', 'diamante'] as const;
  const isValidTier = (t: string): t is typeof VALID_TIERS[number] =>
    (VALID_TIERS as readonly string[]).includes(t);
  const preselectedPackage = isValidTier(packageFromUrl) ? packageFromUrl : null;

  useEffect(() => {
    if (!preselectedPackage) return;
    try {
      sessionStorage.setItem('profileia.pendingPackage', preselectedPackage);
    } catch {
      // sessionStorage might be disabled (private mode / quota) — fail
      // silently. The user can still register and pick a package manually
      // from /app/packages afterward.
    }
  }, [preselectedPackage]);

  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    referralCode: refFromUrl,
  });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [flags, setFlags] = useState<FeatureFlags | null>(null);
  // SPEC-088 §2.1 — T&C checkbox state.
  const [termsAccepted, setTermsAccepted] = useState(false);
  // SPEC-179 P1-3 (Sprint 91 QA): password visibility toggle.
  const [showPassword, setShowPassword] = useState(false);
  // SPEC-179 P0-2 (Sprint 91 QA): live username availability check.
  // States: 'idle' | 'checking' | 'available' | 'taken' | 'invalid'
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');

  // SPEC-179 P0-2 (Sprint 91 QA): debounced username availability check.
  // Waits 500ms after user stops typing, then calls GET /api/auth/check-username.
  // Shows ✓ green if available, ✗ red if taken, ⚠ if invalid format.
  useEffect(() => {
    const username = form.username.trim().toLowerCase();
    if (!username) {
      setUsernameStatus('idle');
      return;
    }
    // Validate format locally first (no API call for invalid format).
    if (!/^[a-z0-9_]{3,32}$/.test(username)) {
      setUsernameStatus('invalid');
      return;
    }
    setUsernameStatus('checking');
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/auth/check-username?username=${encodeURIComponent(username)}`, {
          signal: controller.signal,
        });
        if (!res.ok) return;
        const json = await res.json();
        setUsernameStatus(json.data?.available ? 'available' : 'taken');
      } catch {
        // Network error or aborted — don't block the user.
        setUsernameStatus('idle');
      }
    }, 500);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [form.username]);

  // SPEC-179 P1-4 (Sprint 91 QA): live password strength indicator.
  // Checks: 8+ chars, 1 uppercase, 1 lowercase, 1 number. Returns array of
  // { met: boolean, label: string } for the checklist UI.
  const passwordChecks = [
    { met: form.password.length >= 8, label: '8+ caracteres' },
    { met: /[A-Z]/.test(form.password), label: '1 mayúscula' },
    { met: /[a-z]/.test(form.password), label: '1 minúscula' },
    { met: /[0-9]/.test(form.password), label: '1 número' },
  ];
  const passwordStrength = passwordChecks.filter(c => c.met).length;
  const strengthLabel = passwordStrength === 0 ? '' : passwordStrength <= 2 ? 'Débil' : passwordStrength === 3 ? 'Media' : 'Fuerte';
  const strengthColor = passwordStrength <= 2 ? 'text-red-500' : passwordStrength === 3 ? 'text-amber-500' : 'text-green-500';

  // fix(spec-045-A4-HIGH-002): if no ?ref= URL param was provided, fall back
  // to the `profileia_ref` cookie set by /r/[code] (SPEC-NEGOCIO §8.3 — 30-day
  // cookie). This preserves attribution when the user closes the tab after
  // visiting /r/[code] and later returns directly to /register.
  useEffect(() => {
    if (refFromUrl) return; // URL param wins
    if (typeof document === 'undefined') return;
    const match = document.cookie.match(/(?:^|;\s*)profileia_ref=([^;]+)/);
    if (match && match[1]) {
      try {
        const code = decodeURIComponent(match[1]);
        if (code) setForm((prev) => prev.referralCode ? prev : { ...prev, referralCode: code });
      } catch {
        // Malformed cookie value — ignore silently. The user can still type
        // the code manually.
      }
    }
  }, [refFromUrl]);

  // L-08 fix: Proxy-based FeatureFlags fallback that returns `false` for any
  // property access. Previously ~40 flags were hardcoded to `false`, which
  // meant new flags added to the FeatureFlags type would be `undefined` at
  // runtime (falsy but not `false` — breaks strict equality checks). The
  // Proxy ensures any flag not present on serverFlags resolves to `false`.
  // Moved to module scope (FALSE_FLAGS_PROXY) to avoid re-creating on every render.
  const falseProxy = FALSE_FLAGS_PROXY;

  useEffect(() => {
    api
      .getFeatureFlags()
      .then(setFlags)
      .catch(() => {
        // Si no podemos cargar flags, asumimos register habilitado y resto on
        setFlags({ ...falseProxy, auth_register_enabled: true });
      });
  }, []);

  useEffect(() => {
    // KNOWN: Token store check is one-time on mount. Cross-tab token changes won't trigger redirect.
    if (tokenStore.getAccess()) {
      router.replace('/app');
    }
  }, [router]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    // SPEC-088 §2.2 — T&C acceptance client-side gate. Defense-in-depth:
    // the submit button is disabled when `!termsAccepted`, but a
    // keyboard-triggered submit (e.g. Enter in a focused input) could
    // bypass the disabled button. This early-return guards against that.
    // The backend Zod schema re-validates `termsAccepted === true` so a
    // malicious client that omits the field gets a 400 VALIDATION_ERROR.
    if (!termsAccepted) {
      setFieldErrors({
        terms: 'Debes aceptar los Términos y Condiciones para crear una cuenta.',
      });
      return;
    }

    // BUG-FE-007: client-side zod validation before submit (mirrors backend
    // registerSchema in auth.controller.ts:90). Shows inline errors per field.
    // PR #742 — Founder directive: "si colocan un espacio en el usuario donde
    // fallo eliminarlo y guardar solo los caracteres". Strip ALL whitespace
    // from the username before validation + submit. Also lowercase (matches
    // the backend regex /^[a-z0-9_]{3,32}$/).
    const cleanUsername = form.username.replace(/\s+/g, '').toLowerCase();
    const parsed = registerSchema.safeParse({
      username: cleanUsername,
      email: form.email,
      password: form.password,
      referralCode: form.referralCode || undefined,
      termsAccepted,
    });
    if (!parsed.success) {
      setFieldErrors(formatZodErrors(parsed.error));
      return;
    }

    // Update the form state with the cleaned username so the user sees
    // what was actually submitted (no spaces, lowercase).
    setForm(prev => ({ ...prev, username: cleanUsername }));

    setLoading(true);
    try {
      // FE-004 (Bloque D): `api.register()` (api-client.ts) already handles
      // `tokenStore.setAccess(res.tokens?.accessToken)` internally with
      // optional chaining. Previously this page ALSO called
      // `tokenStore.setAccess(res.tokens.accessToken)` WITHOUT optional
      // chaining — if the backend returned `{ user, verificationRequired: true }`
      // without a `tokens` field (e.g., email verification required flow),
      // `res.tokens.accessToken` threw a TypeError. The catch block then
      // surfaced "Error inesperado" even though registration had succeeded
      // server-side, leaving the user unauthenticated and confused.
      //
      // Fix de raíz: remove the redundant `tokenStore.setAccess` call —
      // `api.register()` is the single source of truth for setting the
      // access token after a successful registration. The page only needs
      // to redirect.
      await api.register({
        username: form.username,
        email: form.email,
        password: form.password,
        referralCode: form.referralCode || undefined,
        // SPEC-088 §2.1 — pass T&C acceptance to backend so it persists
        // `termsAcceptedAt` + `termsVersion` on the new User document.
        termsAccepted,
      });
      // SPEC-050 Bug 5: if the user arrived via `/paquetes` with
      // `?package=<tier>`, we stashed the tier in `sessionStorage`.
      // After successful registration we redirect to `/app/packages?preselect=<tier>`
      // instead of plain `/app` so the user lands on the package
      // purchase flow they came for. For `gratuito` (free tier) we
      // just go to `/app` — there's nothing to purchase, the user
      // already has the free tier by default.
      let pendingPackage: string | null = null;
      try {
        pendingPackage = sessionStorage.getItem('profileia.pendingPackage');
        if (pendingPackage) sessionStorage.removeItem('profileia.pendingPackage');
      } catch {
        // sessionStorage disabled (private mode) — fail silently,
        // fall back to the default /app redirect.
      }
      if (pendingPackage && pendingPackage !== 'gratuito') {
        // PR #994 fix: usar window.location.replace (full reload) en vez de
        // router.replace (client-side navigation). El AuthProvider no se
        // remonta con router.replace, dejando el contexto de auth en estado
        // stale (user=null aunque el token esté en tokenStore). Esto era la
        // causa raíz del bug "nuevo usuario debe logout+login para usar el
        // backoffice". Login page ya usa window.location.replace (línea 294
        // de login/page.tsx) — ahora register también. Consistencia.
        window.location.replace(`/app/packages?preselect=${encodeURIComponent(pendingPackage)}`);
      } else {
        window.location.replace('/app');
      }
    } catch (err) {
      // AUDIT-017 fix (FASE 3 C3): mismo pattern que login — extraer
      // mensaje de forma segura para evitar "[object Object]".
      let errorMessage: string;
      if (err instanceof ApiClientError) {
        errorMessage = err.message || 'Error al crear la cuenta.';
      } else if (err instanceof Error) {
        errorMessage = err.message || 'Error al crear la cuenta.';
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else {
        errorMessage = 'Error inesperado. Intenta de nuevo.';
      }
      setError(errorMessage);
      // v135 fix: también mostrar toast para feedback inmediato
      // (el div role=alert puede no ser visible si el usuario scrolleó)
      toast.error('No se pudo crear la cuenta', {
        description: errorMessage,
        duration: 6000,
      });
    } finally {
      setLoading(false);
    }
  }

  // 4D-MED-002 fix: gate race. Pre-fix, while `flags === null` (the ~200–500ms
  // window between mount and the getFeatureFlags() Promise resolving), the
  // gate `if (flags && !flags.auth_register_enabled)` evaluated to `false`
  // (because `flags` was null) → the registration form rendered and the
  // submit button was clickable. If the user filled + submitted during
  // that window, the backend would 403 (auth_register_enabled=false) on a
  // slow network — confusing UX. Now we render the same Suspense fallback
  // until `flags` resolves, so the form is only interactive after the gate
  // has actually decided whether registration is enabled.
  if (flags === null) {
    return <RegisterFallback />;
  }

  if (!flags.auth_register_enabled) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4 py-12 bg-background">
        <div className="max-w-md text-center bg-card border border-border rounded-2xl p-8">
          <h1 className="text-2xl font-bold mb-3 text-foreground">Registro deshabilitado</h1>
          <p className="text-muted-foreground mb-6">
            El registro de nuevas cuentas está temporalmente deshabilitado.
            Contacta a quien te invitó para más información.
          </p>
          <Button asChild>
            <Link href="/login">Ir a iniciar sesión</Link>
          </Button>
        </div>
      </main>
    );
  }

  // SPEC-050 Bug 1: helper that returns `border-destructive` when the field
  // has an inline error, otherwise leaves the shadcn Input default border
  // (`border-input`, theme-aware) untouched. We must NOT clobber the
  // Input's `dark:bg-input/30` background or `focus-visible:ring-ring`
  // focus ring — pre-fix this page overrode both with hardcoded dark
  // classes (`bg-slate-800`, `focus:ring-[#2563eb]`).
  const inputErrorClass = (hasError: boolean) => (hasError ? 'border-destructive' : '');

  return (
    <main id="main-content" className="min-h-screen flex items-center justify-center px-4 py-12 bg-background relative overflow-hidden">
 {/* P2-3 (Sprint 91 QA): branded background gradient orbs. */}
      <div className="absolute top-20 -left-32 w-96 h-96 bg-[#2563EB]/10 rounded-full blur-3xl" aria-hidden="true" />
      <div className="absolute bottom-10 -right-32 w-96 h-96 bg-[#8B5CF6]/10 rounded-full blur-3xl" aria-hidden="true" />

 {/* P2-2 (Sprint 91 QA): desktop 2-column layout — form left, benefits right. */}
      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-8 items-center relative z-10">
      <div className="w-full max-w-md mx-auto">
 {/* P-198 (): link "Volver al inicio" — visible above the form
            card so the user can return to the public landing without using
            the browser back button. The link sits OUTSIDE the form card so
            it doesn't compete visually with the form's primary CTA. */}
        <div className="mb-6 text-center">
          <Link
            href="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <ChevronLeft className="size-4" />
            Volver al inicio
          </Link>
        </div>

 {/* P1-1 (Sprint 91 QA): H1 = "Crear cuenta" (was "Profileia"). */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Crear cuenta
          </h1>
          <h2 className="text-4xl font-bold tracking-tight mt-2">
            <span className="text-[#06b6d4]">Profile</span>
            <span className="text-[#8b5cf6]">ia</span>
          </h2>
          <p className="text-sm text-muted-foreground mt-2">Crea tu cuenta de emprendedor</p>
        </div>

 {/* P3-1 (Sprint 91 QA): "¿Ya tienes cuenta?" link moved to top. */}
        <div className="mb-4 text-center">
          <p className="text-sm text-muted-foreground">
            ¿Ya tienes cuenta?{' '}
            <Link href="/login" className="text-primary hover:underline font-medium">
              Iniciar sesión
            </Link>
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-card border border-border backdrop-blur rounded-2xl p-8 shadow-2xl"
        >
          <h2 className="text-xl font-semibold mb-6 text-foreground">Crear cuenta</h2>

 {/* Bug 5: pre-selected package banner. Shown when the
              user arrived via `/paquetes` with `?package=<tier>`. Gives
              the user feedback that their package choice is being
              remembered + tells them what happens after registration. */}
          {preselectedPackage && (
            <div className="mb-4 rounded-lg bg-[#06B6D4]/10 border border-[#06B6D4]/30 px-4 py-3 text-sm">
              <p className="font-medium text-[#06B6D4] dark:text-[#22D3EE]">
                Paquete seleccionado: {preselectedPackage.charAt(0).toUpperCase() + preselectedPackage.slice(1)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {preselectedPackage === 'gratuito'
                  ? 'Tu cuenta se creará con el paquete Gratuito. Puedes mejorar cuando quieras.'
                  : 'Al crear la cuenta te llevamos al paso de pago para finalizar la compra.'}
              </p>
            </div>
          )}

 {/* P2-6 (Sprint 91 QA): mini-card with package benefits. */}
          {preselectedPackage && preselectedPackage !== 'gratuito' && (
            <div className="mb-4 rounded-lg bg-[#2563EB]/5 border border-[#2563EB]/20 px-4 py-3 text-xs text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Incluye con {preselectedPackage.charAt(0).toUpperCase() + preselectedPackage.slice(1)}:</p>
              <ul className="space-y-0.5 ml-3">
                {preselectedPackage === 'basic' && <li>· Conference Room + 100 espectadores</li>}
                {preselectedPackage === 'bronce' && <li>· Tarjeta Digital + marca personal</li>}
                {preselectedPackage === 'plata' && <li>· Landing de Captura + captura de prospectos</li>}
                {(preselectedPackage === 'oro' || preselectedPackage === 'platino' || preselectedPackage === 'diamante') && <li>· Marca personalizada en sala + herramientas pro</li>}
              </ul>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-lg bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive" role="alert">
              {error}
            </div>
          )}

          <div className="block mb-4">
            <Label htmlFor="register-username" className="block text-sm font-medium text-foreground mb-1">Nombre de usuario</Label>
            <div className="relative">
              <Input
                id="register-username"
                type="text"
                required
                minLength={3}
                maxLength={32}
                pattern="[a-z0-9_]+"
                title="Solo minúsculas, números y _ (3-32 caracteres)"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="tu_usuario"
                value={form.username}
                onChange={(e) => {
                  // PR #742 — strip whitespace + lowercase as the user types
                  // so they see immediately that spaces are not allowed.
                  const cleaned = e.target.value.replace(/\s+/g, '').toLowerCase();
                  setForm({ ...form, username: cleaned });
                  if (fieldErrors.username) setFieldErrors((p) => ({ ...p, username: '' }));
                }}
                className={`pr-10 ${inputErrorClass(!!fieldErrors.username)}`}
              />
 {/* P0-2 (Sprint 91 QA): live username status icon. */}
              {usernameStatus === 'checking' && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 size-4 animate-spin text-muted-foreground" aria-hidden="true" />
              )}
              {usernameStatus === 'available' && (
                <Check className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-green-500" aria-label="Usuario disponible" />
              )}
              {usernameStatus === 'taken' && (
                <X className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-red-500" aria-label="Usuario no disponible" />
              )}
              {usernameStatus === 'invalid' && (
                <X className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-amber-500" aria-label="Formato inválido" />
              )}
            </div>
 {/* P0-2: status text below input. */}
            {usernameStatus === 'available' && (
              <span className="block text-xs text-green-600 mt-1">✓ Usuario disponible</span>
            )}
            {usernameStatus === 'taken' && (
              <span className="block text-xs text-red-600 mt-1">✗ Este usuario ya está en uso</span>
            )}
            {usernameStatus === 'invalid' && (
              <span className="block text-xs text-amber-600 mt-1">⚠ Solo minúsculas, números y _ (3-32 caracteres)</span>
            )}
            {fieldErrors.username && (
              <span className="block text-xs text-destructive mt-1">{fieldErrors.username}</span>
            )}
          </div>

          <div className="block mb-4">
            <Label htmlFor="register-email" className="block text-sm font-medium text-foreground mb-1">Correo electrónico</Label>
            <Input
              id="register-email"
              type="email"
              required
              autoComplete="email"
              placeholder="tu@email.com"
              value={form.email}
              onChange={(e) => {
                setForm({ ...form, email: e.target.value });
                if (fieldErrors.email) setFieldErrors((p) => ({ ...p, email: '' }));
              }}
              className={inputErrorClass(!!fieldErrors.email)}
            />
            {fieldErrors.email && (
              <span className="block text-xs text-destructive mt-1">{fieldErrors.email}</span>
            )}
          </div>

          <div className="block mb-4">
            <Label htmlFor="register-password" className="block text-sm font-medium text-foreground mb-1">
              Contraseña
            </Label>
            <div className="relative">
              <Input
                id="register-password"
                type={showPassword ? 'text' : 'password'}
                required
                minLength={8}
                autoComplete="new-password"
                placeholder="••••••••"
                value={form.password}
                onChange={(e) => {
                  setForm({ ...form, password: e.target.value });
                  if (fieldErrors.password) setFieldErrors((p) => ({ ...p, password: '' }));
                }}
                className={`pr-10 ${inputErrorClass(!!fieldErrors.password)}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                aria-pressed={showPassword}
              >
                {showPassword ? <EyeOff className="size-4" aria-hidden="true" /> : <Eye className="size-4" aria-hidden="true" />}
              </button>
            </div>
 {/* P1-4 (Sprint 91 QA): live password strength checklist. */}
            {form.password && (
              <div className="mt-2 space-y-1">
                <div className={`text-xs font-medium ${strengthColor}`}>Fortaleza: {strengthLabel}</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                  {passwordChecks.map((check) => (
                    <div key={check.label} className={`flex items-center gap-1 text-xs ${check.met ? 'text-green-600' : 'text-muted-foreground'}`}>
                      {check.met ? <Check className="size-3" aria-hidden="true" /> : <span className="size-3 inline-block" aria-hidden="true" />}
                      {check.label}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {fieldErrors.password && (
              <span className="block text-xs text-destructive mt-1">{fieldErrors.password}</span>
            )}
          </div>

          <div className="block mb-6">
            <Label htmlFor="register-referral-code" className="block text-sm font-medium text-foreground mb-1">
              Código de referido (opcional)
            </Label>
            <Input
              id="register-referral-code"
              type="text"
              maxLength={32}
              autoComplete="off"
              placeholder="ABC123"
              value={form.referralCode}
              onChange={(e) => {
                setForm({ ...form, referralCode: e.target.value });
                if (fieldErrors.referralCode) setFieldErrors((p) => ({ ...p, referralCode: '' }));
              }}
              className={inputErrorClass(!!fieldErrors.referralCode)}
            />
 {/* P3-3 (Sprint 91 QA): tooltip/helper text. */}
            <span className="block text-xs text-muted-foreground mt-1">
              Pídelo a quien te invitó a Profileia
            </span>
            {fieldErrors.referralCode && (
              <span className="block text-xs text-destructive mt-1">{fieldErrors.referralCode}</span>
            )}
          </div>

 {/* — T&C acceptance checkbox.
              - `Checkbox` is the shadcn component (same as checkout-modal + packages page).
              - `Label htmlFor` uses the explicit-ID pattern (D-LOW-003 fix) for WCAG SC 1.3.1 + 4.1.2.
              - Link opens `/legal/terminos` in a NEW TAB so the user keeps form state.
              - The submit button below is disabled until `termsAccepted === true`.
              - Pure UX gating — no business logic. The legally-binding text
                lives in `frontend/src/lib/legal-content.ts` (USER_TERMS_AND_CONDITIONS),
                drafted by IA Abogada. P-083: 0 internal doc refs in the label string.
 - P-197 (): the checkbox container is visually highlighted
                (border-2 + bg-muted) + the checkbox is size-5 (20×20px instead
                of the default 16×16px) so the user can't miss it. The whole
                container is wrapped in a single <label> so clicking anywhere
                toggles the checkbox (better UX for non-technical users on
                mobile — the touch target is the whole container, not just
                the 16px checkbox). ExternalLink icon next to the T&C link
                signals it opens in a new tab. */}
          <div className="block mb-6">
            <label
              htmlFor="register-terms-accepted"
              className="flex items-start gap-3 p-3 rounded-lg border-2 border-border bg-muted/30 hover:bg-muted/50 hover:border-primary/40 transition-colors cursor-pointer"
            >
              <Checkbox
                id="register-terms-accepted"
                checked={termsAccepted}
                onCheckedChange={(checked) => {
                  setTermsAccepted(checked === true);
                  if (fieldErrors.terms) setFieldErrors((p) => ({ ...p, terms: '' }));
                }}
                required
                className="mt-0.5 size-5 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <span className="text-sm font-normal leading-relaxed">
                <span className="font-medium text-foreground">He leído y acepto los{' '}</span>
                <Link
                  href="/legal/terminos"
                  target="_blank"
                  className="text-primary hover:underline font-semibold inline-flex items-center gap-0.5"
                >
                  Términos y Condiciones
                  <ExternalLink className="size-3 inline" />
                </Link>
                <span className="font-medium text-foreground">{' '}de Profileia.</span>
              </span>
            </label>
            {fieldErrors.terms && (
              <p className="text-xs text-destructive mt-1.5 ml-1">{fieldErrors.terms}</p>
            )}
          </div>

 {/* P1-6 (Sprint 91 QA): hint when button is disabled. */}
          {!termsAccepted && (
            <p className="text-xs text-muted-foreground text-center mb-3">
              Acepta los Términos y Condiciones para continuar
            </p>
          )}

          <Button
            type="submit"
            disabled={loading || !termsAccepted}
            className="w-full bg-gradient-to-r from-[#2563eb] to-[#8b5cf6] text-white hover:opacity-90 shadow-lg"
          >
 {/* P2-1 (Sprint 91 QA): spinner Loader2 during submit. */}
 {/* P2-5 (Sprint 91 QA): dynamic button text with package. */}
            {loading ? (
              <>
                <Loader2 className="size-4 mr-2 animate-spin" aria-hidden="true" />
                Creando…
              </>
            ) : preselectedPackage && preselectedPackage !== 'gratuito' ? (
              `Crear cuenta con ${preselectedPackage.charAt(0).toUpperCase() + preselectedPackage.slice(1)}`
            ) : (
              'Crear cuenta'
            )}
          </Button>

 {/* P3-2 (Sprint 91 QA): microcopy about post-registration flow. */}
          <p className="mt-4 text-center text-xs text-muted-foreground">
            {preselectedPackage && preselectedPackage !== 'gratuito'
              ? 'Al crear la cuenta te llevamos al paso de pago. Te enviaremos un email de verificación.'
              : 'Te enviaremos un email de verificación. Mientras tanto, puedes explorar tu cuenta.'}
          </p>

          <p className="mt-4 text-center text-sm text-muted-foreground">
            ¿Ya tienes cuenta?{' '}
            <Link href="/login" className="text-primary hover:underline">
              Iniciar sesión
            </Link>
          </p>
        </form>
      </div>

 {/* P2-2 (Sprint 91 QA): right column benefits panel (desktop only). */}
      <div className="hidden lg:flex flex-col justify-center px-8 py-12">
        <h2 className="text-3xl font-bold tracking-tight text-foreground mb-6">
          Únete al movimiento
        </h2>
        <p className="text-muted-foreground mb-8">
          La casa del emprendedor. Todo lo que necesitas para emprender en un solo lugar.
        </p>
        <ul className="space-y-4">
          <li className="flex items-start gap-3">
            <span className="size-6 rounded-full bg-[#2563EB]/10 text-[#2563EB] flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">1</span>
            <div>
              <p className="font-medium text-foreground">Cuenta gratuita</p>
              <p className="text-sm text-muted-foreground">Sin tarjeta de crédito. 10 espectadores + Capacitaciones Profileia.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="size-6 rounded-full bg-[#06B6D4]/10 text-[#06B6D4] flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">2</span>
            <div>
              <p className="font-medium text-foreground">0% comisión en depósitos</p>
              <p className="text-sm text-muted-foreground">Tu saldo llega completo a tu cuenta Profileia.</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="size-6 rounded-full bg-[#8B5CF6]/10 text-[#8B5CF6] flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">3</span>
            <div>
              <p className="font-medium text-foreground">Plan de compensación transparente</p>
              <p className="text-sm text-muted-foreground">Bono directo $10 + matriz viva + viaje nacional Diamante.</p>
            </div>
          </li>
        </ul>
        <blockquote className="mt-8 pl-4 border-l-2 border-[#2563EB]/30 text-sm text-muted-foreground italic">
          “Antes pagaba 5 herramientas que no se hablaban entre sí. Hoy todo vive en mi enlace.”
          <footer className="mt-1 not-italic text-xs">— Testimonio pronto</footer>
        </blockquote>
      </div>
      </div>
    </main>
  );
}
